/**
 * Datapad - Main application component.
 *
 * @remarks
 * This component orchestrates the loading of the Fluid container and SharedTree,
 * sets up the application context, and renders the appropriate UI based on the
 * current state.
 */
import { ErrorScreen, LoadingScreen } from '@datapad/common-controls';
import {
	AppContext,
	type CalendarDate,
	type Character,
	type Datapad as DatapadTree,
	GameMaster,
	type PermissionLevel,
	PresenceContext,
	type PresenceManager,
	TreeReference,
	useAppContext,
	useAppContextProvider,
	type UserContext,
} from '@datapad/data-model';
import { Box, Drawer } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { type IFluidContainer, Tree, type TreeView } from 'fluid-framework';
import React from 'react';
import { DatapadAppStateContext, FluidContainerContext } from '../../../utilities';
import type { Actions } from '../Actions';
import { AppId, getFirstAvailableApp, isAppAvailable } from '../AppId';
import { useFluidContainer, useTreeView } from '../hooks/index.js';
import type { AppState } from '../State';
import { Banner } from './Banner';
import { Calendar } from './Calendar';
import { Campaigns } from './Campaigns';
import { Contacts } from './Contacts';
import { DatapadPaper } from './DatapadPaper';
import { GalaxyMap } from './GalaxyMap';
import { DatapadMenu } from './menu';
import { Players } from './Players';
import { Profile as ProfileApp } from './Profile';
import { Timeline } from './Timeline';

// #region Constants

/**
 * Default app selection to be used within the datapad.
 */
export const defaultAppSelection = AppId.Profile;

const appId = 'datapad';
const viewId = 'datapad-view';
const menuId = 'datapad-menu';

// #endregion

// #region Types

/**
 * External props passed to the Datapad from the parent application.
 */
export interface DatapadExternalProps {
	/**
	 * Name of the signed-in user.
	 */
	userName: string;

	/**
	 * Function for signing the user out of the application.
	 */
	logoutFunction: () => void;

	/**
	 * Currently selected application.
	 */
	appSelection: AppId;

	/**
	 * Function to call when the app selection needs to change.
	 */
	onChangeAppSelection: (appId: AppId, urlSearchParams?: string) => void;
}

/**
 * Combined props for the Datapad component.
 */
export type DatapadProps = Actions & AppState & DatapadExternalProps;

// #endregion

// #region Utility Functions

/**
 * Determines the user context based on the signed-in user and the datapad data.
 */
function determineUserContext(
	datapad: DatapadTree,
	userName: string,
	sessionCharacter: TreeReference | undefined,
	sessionCampaign: TreeReference | undefined,
): UserContext {
	// Find the user in the players map
	const user = datapad.getUserByName(userName);

	if (user === undefined) {
		// User not found - they are a guest
		console.log('User not recognized. Signing in as guest.');
		return { permissionLevel: 'guest' };
	}

	// Check if the player is a GameMaster
	if (Tree.is(user, GameMaster)) {
		console.log('GM user recognized. Welcome Game Master.');

		// GMs can view any character. Use session character if specified, otherwise first character.
		const allCharacters = datapad.getAllCharacters();
		const character = sessionCharacter
			? allCharacters.find((character) => sessionCharacter.isReferenceTo(character))
			: allCharacters[0];

		// Find campaigns this character belongs to
		const characterCampaigns =
			character !== undefined
				? [...datapad.campaigns.values()].filter((c) =>
						[...c.activeCharacters.values()].some((ref) =>
							ref.isReferenceTo(character),
						),
					)
				: [];

		// Use session campaign if specified and valid, otherwise use first campaign
		const campaign = sessionCampaign
			? characterCampaigns.find((c) => sessionCampaign.isReferenceTo(c))
			: characterCampaigns[0];

		return {
			permissionLevel: 'game-master',
			user: TreeReference.createReferenceToNode(user),
			character: character ? TreeReference.createReferenceToNode(character) : undefined,
			campaign: campaign ? TreeReference.createReferenceToNode(campaign) : undefined,
		};
	}

	// Regular player - find their character and campaign
	const playerCharacters = datapad.getPlayersCharacters(user);

	// Use session character if specified, otherwise use default character, otherwise first character
	const character = sessionCharacter
		? playerCharacters.find((c) => sessionCharacter.isReferenceTo(c))
		: user.defaultCharacter
			? datapad.characters.get(user.defaultCharacter.id)
			: playerCharacters[0];

	if (character === undefined) {
		// Player has no characters - treat as guest for now
		console.warn(`Player "${userName}" has no characters. Treating as guest.`);
		return { permissionLevel: 'guest' };
	}

	// Find campaigns this character belongs to
	const characterCampaigns = [...datapad.campaigns.values()].filter((c) =>
		[...c.activeCharacters.values()].some((ref) => ref.isReferenceTo(character)),
	);

	// Use session campaign if specified and valid, otherwise use first campaign
	const campaign = sessionCampaign
		? characterCampaigns.find((c) => sessionCampaign.isReferenceTo(c))
		: characterCampaigns[0];

	console.log(`Player user ${userName} recognized. Signing in as ${character.name}.`);
	return {
		permissionLevel: 'player',
		user: TreeReference.createReferenceToNode(user),
		character: TreeReference.createReferenceToNode(character),
		campaign: campaign ? TreeReference.createReferenceToNode(campaign) : undefined,
	};
}

// #endregion

// #region Main Component

/**
 * Datapad main entry-point.
 *
 * @remarks
 * This component handles the complete loading flow:
 * 1. Load the Fluid container
 * 2. Create the SharedTree view
 * 3. Set up the application context
 * 4. Render the UI
 *
 * Loading and error states are handled with appropriate screens.
 */
export function Datapad(props: DatapadProps): React.ReactElement {
	const { userName } = props;

	// Load Fluid container
	const {
		container,
		presenceManager,
		isLoading: containerLoading,
		error: containerError,
	} = useFluidContainer(userName);

	// Create tree view from container
	const {
		treeView,
		isLoading: treeLoading,
		error: treeError,
		isIncompatible,
	} = useTreeView(container);

	// Render loading states
	let content: React.ReactElement;

	if (containerLoading) {
		content = <LoadingScreen text="Loading system data..." />;
	} else if (containerError) {
		content = (
			<ErrorScreen
				textLines={['Failed to connect to the data service.', containerError.message]}
			/>
		);
	} else if (treeLoading) {
		content = <LoadingScreen text="Initializing data view..." />;
	} else if (treeError) {
		content = (
			<ErrorScreen textLines={['Failed to initialize data view.', treeError.message]} />
		);
	} else if (isIncompatible) {
		content = (
			<ErrorScreen
				textLines={[
					'There was an error loading the application data: Incompatible tree schema.',
				]}
			/>
		);
	} else if (!treeView || !container || !presenceManager) {
		// This shouldn't happen, but handle it gracefully
		content = <ErrorScreen textLines={['An unexpected error occurred.']} />;
	} else {
		// Everything loaded successfully - render the app content
		content = (
			<DatapadContent
				{...props}
				container={container}
				presenceManager={presenceManager}
				treeView={treeView}
			/>
		);
	}

	return (
		<DatapadPaper>
			<Box
				id={appId}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					textAlign: 'center',
					flex: 1,
					minHeight: 0,
				}}
			>
				{content}
			</Box>
		</DatapadPaper>
	);
}

// #endregion

// #region Content Component

/**
 * Props for {@link DatapadContent}.
 */
interface DatapadContentProps extends DatapadProps {
	container: IFluidContainer;
	presenceManager: PresenceManager;
	treeView: TreeView<typeof DatapadTree>;
}

/**
 * Datapad content component - renders once all resources are loaded.
 *
 * @remarks
 * This component sets up:
 * - Session state (character, campaign, permission level)
 * - User context and app context
 * - Context providers for child components
 */
function DatapadContent(props: DatapadContentProps): React.ReactElement {
	const { appSelection, container, presenceManager, treeView, userName, onChangeAppSelection } =
		props;

	const datapad = treeView.root;

	// Session state
	const [sessionCharacter, setSessionCharacter] = React.useState<TreeReference | undefined>(
		undefined,
	);
	const [sessionCampaign, setSessionCampaign] = React.useState<TreeReference | undefined>(
		undefined,
	);
	const [effectivePermissionLevel, setEffectivePermissionLevel] = React.useState<
		PermissionLevel | undefined
	>(undefined);

	// Determine user context
	const userContext = determineUserContext(datapad, userName, sessionCharacter, sessionCampaign);

	// Get the user's characters for navigation handling
	const user = React.useMemo(() => datapad.getUserByName(userName), [datapad, userName]);

	const userCharacters = React.useMemo(() => {
		if (user === undefined) {
			return [];
		}
		if (Tree.is(user, GameMaster)) {
			return datapad.getAllCharacters();
		}
		return datapad.getPlayersCharacters(user);
	}, [datapad, user]);

	// Navigation callbacks
	const handleCharacterLink = React.useCallback(
		(character: Character | TreeReference) => {
			console.log(`Handling link to character with ID ${character.id}...`);
			const isOwnCharacter = userCharacters.some((pc) => pc.id === character.id);

			if (isOwnCharacter) {
				if (!sessionCharacter || sessionCharacter.id !== character.id) {
					setSessionCharacter(new TreeReference({ id: character.id }));
				}
				console.log(
					`Selected character is player's own character. Navigating to Profile App.`,
				);
				onChangeAppSelection(AppId.Profile, undefined);
			} else {
				onChangeAppSelection(AppId.Contacts, `id=${character.id}`);
			}
		},
		[onChangeAppSelection, userCharacters, sessionCharacter],
	);

	const handleCalendarLink = React.useCallback(
		(date: CalendarDate) => {
			onChangeAppSelection(AppId.Calendar, date.toUrlSearch());
		},
		[onChangeAppSelection],
	);

	// Create app context
	const appContextData = useAppContextProvider(datapad, userContext, {
		handleCharacterLink,
		handleCalendarLink,
		effectivePermissionLevel,
		setEffectivePermissionLevel,
	});

	// Broadcast presence info when user context, session character, or app selection changes
	const userId = userContext.permissionLevel !== 'guest' ? userContext.user?.id : undefined;
	const sessionCharacterId =
		userContext.permissionLevel !== 'guest' ? userContext.character?.id : undefined;
	const currentApp = AppId[appSelection];
	React.useEffect(() => {
		presenceManager.updateMyInfo({ userId, sessionCharacterId, currentApp });
	}, [presenceManager, userId, sessionCharacterId, currentApp]);

	return (
		<FluidContainerContext.Provider value={container}>
			<PresenceContext.Provider value={presenceManager}>
				<AppContext.Provider value={appContextData}>
					<DatapadAppShell
						{...props}
						sessionCharacter={sessionCharacter}
						setSessionCharacter={setSessionCharacter}
						setSessionCampaign={setSessionCampaign}
					/>
				</AppContext.Provider>
			</PresenceContext.Provider>
		</FluidContainerContext.Provider>
	);
}

// #endregion

// #region App Shell Component

/**
 * Props for {@link DatapadAppShell}.
 */
interface DatapadAppShellProps extends DatapadProps {
	sessionCharacter: TreeReference | undefined;
	setSessionCharacter: (character: TreeReference | undefined) => void;
	setSessionCampaign: (campaign: TreeReference | undefined) => void;
}

/**
 * Datapad app shell - renders the menu and current app.
 */
function DatapadAppShell(props: DatapadAppShellProps): React.ReactElement {
	const { appSelection, onChangeAppSelection } = props;
	const appContext = useAppContext();

	// App selection change handler
	const changeApp = React.useCallback(
		(
			newAppSelection: AppId,
			urlSearchParams: string | undefined,
			toggleMenuState: 'open' | 'closed' | undefined,
		): void => {
			if (newAppSelection !== appSelection || urlSearchParams) {
				onChangeAppSelection(newAppSelection, urlSearchParams);
			}
			if (toggleMenuState !== undefined) {
				props.setDatapadMenuState(toggleMenuState);
			}
		},
		[appSelection, onChangeAppSelection, props],
	);

	// Redirect to an available app if the current app is unavailable
	const effectivePermissionLevel = appContext.getEffectivePermissionLevel();
	React.useEffect(() => {
		if (!isAppAvailable(appSelection, effectivePermissionLevel)) {
			const fallbackApp = getFirstAvailableApp(effectivePermissionLevel);
			onChangeAppSelection(fallbackApp, undefined);
		}
	}, [appSelection, effectivePermissionLevel, onChangeAppSelection]);

	// Render the selected app
	const app = React.useMemo(() => {
		switch (appSelection) {
			case AppId.Profile:
				return <ProfileApp />;
			case AppId.GalaxyMap:
				return <GalaxyMap />;
			case AppId.Contacts:
				return <Contacts />;
			case AppId.Calendar:
				return <Calendar />;
			case AppId.Timeline:
				return <Timeline />;
			case AppId.Players:
				return <Players />;
			case AppId.Campaigns:
				return <Campaigns />;
			default:
				throw new Error(`Unrecognized app selection: ${appSelection}`);
		}
	}, [appSelection]);

	return (
		<DatapadAppStateContext.Provider
			value={{
				showMenu: () => props.setDatapadMenuState('open'),
				hideMenu: () => props.setDatapadMenuState('closed'),
				changeAppSelection: changeApp,
			}}
		>
			<Banner onOpenMenu={() => props.setDatapadMenuState('open')} />

			{/* Main content area */}
			<Box
				id={viewId}
				sx={{
					textAlign: 'center',
					flex: 1,
					minHeight: 0,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'auto',
				}}
			>
				{app}
			</Box>

			{/* Side menu drawer */}
			<Drawer
				id={menuId}
				onClose={() => props.setDatapadMenuState('closed')}
				open={props.menuState === 'open'}
				PaperProps={{
					sx: (theme) => ({
						bgcolor: alpha(theme.palette.background.paper, 0.85),
						backdropFilter: 'blur(10px)',
					}),
				}}
			>
				<DatapadMenu
					appSelection={appSelection}
					hideMenu={() => props.setDatapadMenuState('closed')}
					logout={props.logoutFunction}
					changeSessionCharacter={props.setSessionCharacter}
					changeSessionCampaign={props.setSessionCampaign}
				/>
			</Drawer>
		</DatapadAppStateContext.Provider>
	);
}

// #endregion

export default Datapad;
